import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'lodash';
import i18n from 'react-intl-universal';
import EditorTitle from './EditorTitle';
import styles from './RichTextEditor.scss';

import ImageUploader from '../../assets/vendors/ImageUploader';
import Dante from '../../assets/vendors/dante.no.css';

import Message from '../../components/Message';
import EditorToolBar from '../../components/EditorToolBar/EditorToolBar';
import TimePublishToolbar from '../TimePublishToolbar/TimePublishToolbar';
import { Global, Notes, User } from '../../constants/actionTypes';
import { publishNote, privateNote, updateNote } from '../../actions/NotesAction';
import { updateNoteContentAbbr, getAudioUrl } from '../../actions/NoteContentAction';
import { EditorLinkModal, EditorImgModal, EditorVideoModal, EditorAudioModal, EditorFormulaModal, EditorFilmModal, getFilmScoreText } from '../../components/EditorModal/';
import Modal from '../../components/Modal';
import { setPnAgreeModalData } from '../../actions/GlobalAction';
import { getState } from '../../reducers/NoteContent/NoteContentReducer';
import { getState as getNotesState } from '../../reducers/Notes/NotesReducer';
import { getState as getUserState } from '../../reducers/User/UserReducer';
import { calcAbbr, keyDownResult, getChildrenNum, martinDispatch, captureException, cleanTheNoteHtml } from '../../utils/util';
import { getPNSettingPageUrl } from '../../utils/urlHelper';
import { MAX_VIDEO_COUNT, JIANSHU_EDITOR } from '../../constants/variables';

const curProtocol = window.location.protocol || '';

@withRouter
@connect(
  state => ({
    content: getState(state),
    notes: getNotesState(state),
    userInfo: getUserState(state).get('user'),
  }),
  dispatch => ({
    publishNote: (noteId, forcedPublish) => dispatch(publishNote(noteId, forcedPublish)),
    privateNote: noteId => dispatch(privateNote(noteId)),
    updateNote: (noteId, title, content) => dispatch(updateNote(noteId, title, content)),
    updateNoteContentAbbr: (noteId, abbr, wordNum) => {
      dispatch(updateNoteContentAbbr(noteId, abbr, wordNum));
    },
    setPnAgreeModalData: (show, data) => dispatch(setPnAgreeModalData(show, data)),
    getAudioUrl: noteId => dispatch(getAudioUrl(noteId)),
  }),
)
export default class RichTextEditor extends Component {
  static propTypes = {
    isWritingMode: PropTypes.bool.isRequired, // 当前所在是否为全屏模式
  };

  static MAX_FILE_SIZE = 10; // 图片最大M
  constructor(props) {
    super(props);
    this.state = {
      hasSelectLink: false,
      isDraggingOver: false, // 是否正在拖拽文件到editor
      isContentChanged: false,
      linkModalVisible: false,
      imgModalVisible: false,
      videoModalVisible: false,
      audioModalVisible: false,
      formulaModalVisible: false,
      filmModalVisible: false,
      isPublishing: false,
      isAutoSaving: false,
      wordNum: 0, // 文章字数
      curLinkInfo: {}, // 编辑器当前选中的链接信息
      error: false, // 组件错误，防止白屏
      isAudioExist: this.props.content.getIn(['entities', this.props.match.params.noteId, 'audio', 'title']) !== undefined,
      isAudioPlaying: false,
      initFormula: '',
    };
  }

  componentDidMount() {
    const { notes, match } = this.props;
    const { params: { noteId } } = match;
    this.initEditorWithTryCatch(noteId);
    this.setNoteAllStatus();
    window[JIANSHU_EDITOR] = this;
  }

  componentWillReceiveProps(nextProps) {
    const { notes, match, content } = nextProps;
    const { params: { noteId } } = match;
    if (this.polling) {
      clearTimeout(this.polling);
    }
    // 切换文章时
    if (this.props.match.params.noteId !== noteId) {
      this.setState({
        isAudioPlaying: false,
      });
      this.initEditorWithTryCatch(noteId, nextProps);
    }
    this.setNoteAllStatus(nextProps);
    if (content.getIn(['entities', noteId, 'audio', 'title']) !== '' && content.getIn(['entities', noteId, 'audio', 'play_url']) === '') {
      this.polling = setTimeout(() => { this.props.getAudioUrl(noteId); }, 10000);
    }
  }

  componentWillUnmount() {
    if (this.editor) {
      this.editor.destroy();
    }
    if (this.polling) {
      clearTimeout(this.polling);
    }
    window[JIANSHU_EDITOR] = null;
    this.editor.$editElem[0].removeEventListener('click', this.changeFormulaImg);
  }

  changeFormulaImg = (e) => {
    if (e.target.className === 'math-inline') {
      this.editor.exec('changeFormula', e);
      this.openFormulaModal(e.target.alt);
    }
  }

  // 设置文章的各种状态，isPublishing/isAutoSaving/isContentChanged
  setNoteAllStatus = (props = this.props) => {
    const { notes, match, content } = props;
    const { params: { noteId } } = match;
    // eslint-disable-next-line camelcase
    const { content_updated_at, last_compiled_at } = this.getNote(props).toJS();
    const noteContentStatus = content.getIn(['entities', noteId, 'status']);
    const audioStatus = content.getIn(['entities', noteId, 'audio', 'title']) !== undefined;
    this.setState({
      isContentChanged: content_updated_at > last_compiled_at, // eslint-disable-line camelcase
      // 正在发布或者正在获取用户手机信息都应该显示正在发布
      isPublishing: noteContentStatus === Notes.PUBLISH_NOTE,
      isAutoSaving: noteContentStatus === Notes.UPDATE_NOTE,
      isAudioExist: audioStatus,
    });
  }
  updateAbbr = (props = this.props) => {
    const { match } = props;
    const { params: { noteId } } = match;
    // 更新abbr和字数
    const contentText = this.editor.getText();
    const { abbr, wordNum } = calcAbbr(contentText);
    this.setState({
      wordNum,
    });
    this.props.updateNoteContentAbbr(noteId, _.unescape(abbr), wordNum);
  };

  // 文章内容或者是标题发生了变化，需要服务端保存
  saveNote = (title = this.noteTitle, content = this.noteContent, isForce = false, noteIdParam) => {
    const { match } = this.props;
    const { params: { nbId } } = match;
    const noteId = noteIdParam || match.params.noteId;
    const { isAutoSaving } = this.state;
  
    // 添加 URL 计数逻辑
    const urlCount = this.countUrlsInContent(content);
    const member = this.props.userInfo.get('member');
    
    //console.log(`Number of URLs in content: ${urlCount}`);
    //console.log('member:', member);
    if (!member && urlCount > 2) {
      Modal.Alert('对不起，您的文章中包含链接多于2个。', {
            onOk: () => {
              console.log('ok alert');
            },
            top: '45%',
          });
      return; // 停止执行后续代码
    }

    // 如果没有变化就不需要保存, 强制保存除外
    if (!isForce && this.noteTitle === title && this.noteContent === content) {
      return;
    }
    // cache the title and content
    this.noteTitle = title;
    this.noteContent = content;
    // 只要有变化就需要更新abbr，保证用户实时看到自己的文章变化
    this.updateAbbr();
    // 正在保存就没必要再保存了，这是错误的，因为这样有可能造成用户数据丢失
    // if (isAutoSaving) {
    //   return;
    // }
    // 发布页正文段落默认左对齐，而 Dante 会剥掉顶层节点的内联 style，
    // 故在保存字符串里给评分段落注入居中样式；服务端编译若不保留则退化为左对齐，位置仍在海报正下方
    const savedContent = content.replace(/<p class="film-score">/g, '<p class="film-score" style="text-align: center;">');
    // 重点处理这个错误
    try {
      this.props.updateNote(noteId, title, savedContent);
    } catch (e) {
      // 上报react error
      const note = this.getNote();
      captureException(e, {
        noteId,
        nbId,
        note: note && note.toJS(),
      });
    }
    this.setState({
      isContentChanged: true,
      urlCount, // 更新状态以存储 URL 数量
    });
  }

  // 新增方法：计算 content 中的 URL 数量
  countUrlsInContent = (content) => {
  // 修改正则表达式以支持无协议头的 URL
    const urlRegex = /https?:\/\/[\w.-]+(?:\/[\w.-]*)*|\b(?:[\w-]+(?<!\d)\.){2,}[\w-]+(?:\/[\w.-]*)?/g;
    const matches = content.match(urlRegex);
     // 可选：过滤掉常见的静态资源文件
    const imageOrStaticFile = /\.(?:jpe?g|png|gif|bmp|svg|webp|css|js|ico|pdf)$/i;
    // 电影详情链接由"插入电影"功能自动包裹在海报上，属于站内跳转，不计入用户外链数量
    const filmDetailUrl = /^https?:\/\/(?:www\.)?jianshu\.com\/film\//i;
    if(matches){
      const isCounted = url => !imageOrStaticFile.test(url) && !filmDetailUrl.test(url);
      const filteredUrls = matches.filter(isCounted);
      //console.log('filteredUrls:', filteredUrls);
      return filteredUrls ? filteredUrls.length : 0;
    }
    return 0;
    
  }

  onTitleChanged = ({ title, noteId }, isInit) => {
    // 第一次初始化标题
    if (isInit) {
      this.noteTitle = title;
      return;
    }
    this.saveNote(title, this.getContent(noteId), false, noteId);
  }
  onContentChanged = (preContent, curContent) => {
    this.saveNote(this.noteTitle, curContent);
  }
  changeAudio = () => {
    const { match: { params: { noteId } } } = this.props;
    this.saveNote(undefined, undefined, true);
  }
  initEditorWithTryCatch = (noteId, props = this.props) => {
    try {
      this.initEditor(noteId, props);
    } catch (error) {
      captureException(error, {
        noteId,
        hh: '文章初始化错误',
      });
    }
  }
  initEditor = (noteId, props = this.props) => {
    // 先销毁前面新建的
    if (this.editor && this.editor.destroy) {
      this.editor.destroy();
    }
    this.noteContent = this.getContent(noteId);
    this.setState({
      isContentChanged: false, // reset
    });
    const events = Dante.Events;
    // 初始化富文本
    this.editor = new Dante('#editor', {
      editorClass: 'kalamu-area',
      content: this.noteContent,
      autoFillParentSize: false, // 让其自然展开，方便后续实现回车光标不掉落
      fileLimitSize: RichTextEditor.MAX_FILE_SIZE * 1024 * 1024,
      protocol: curProtocol,
    });
    this.editor.on(events.HTML_CONTENT_CHANGE, this.onContentChanged);
    this.editor.on(events.IMG_UPLOAD, (opts) => {
      this.uploadImageForDante(opts);
    });
    this.editor.on(events.DRAG_ENTER, this.handleDragEnterForDante);
    this.editor.on(events.DRAG_LEAVE, this.handleDragLeaveForDante);
    this.editor.on(events.DROP, this.handleDropForDante);
    this.editor.on(events.ERROR, this.handleErrorForDante);
    this.editor.$editElem[0].addEventListener('click', this.changeFormulaImg);
    this.updateAbbr(props);
  }
  handleDragEnterForDante = () => {
    this.setState({
      isDraggingOver: true,
    });
  }
  handleDragLeaveForDante = () => {
    this.setState({
      isDraggingOver: false,
    });
  }
  handleDropForDante = () => {
    this.setState({
      isDraggingOver: false,
    });
  }
  alertUploadError = () => {
    Modal.Alert((
      <span>
        {i18n.get('error.upload-fail')}
        <a
          href="https://www.jianshu.com/p/acb660ddc619"
          target="_blank"
          rel="noopener noreferrer"
        >
          {i18n.get('error.click')}
        </a>
        。
      </span>
    ));
  }
  uploadImageForDante = (img) => {
    // ImageUploader组件没有正确的选择优先级，所以只能传一个字段
    if (img.url) {
      img = { url: img.url, id: img.id, islast: img.islast }; // eslint-disable-line
    } else {
      img = { file: img.file, id: img.id, islast: img.islast }; // eslint-disable-line
    }
    const uploader = new ImageUploader([img], {
      size: RichTextEditor.MAX_FILE_SIZE * 1024 * 1024, // 10MB
      'x:protocol': curProtocol.slice(0, -1),
    });
    uploader.upload(img).then((data) => {
      this.editor.imageUploadSuccess({
        id: img.id,
        data,
        islast: img.islast,
      });
    }).catch((err) => {
      this.editor.imageUploadFail({
        id: img.id,
        data: {
          msg: err.message,
          code: err.code,
        },
      });
      this.alertUploadError();
    });
  }
  handleErrorForDante = (data = {}) => {
    const errors = Dante.Errors;
    switch (data.code) {
      case errors.FILE_NOT_EXIST:
        Message.error(i18n.get('error.file-not-exit'));
        break;
      case errors.FILE_TYPE_ERROR:
        Message.error(i18n.get('error.file-type-error'));
        break;
      case errors.FILE_TOO_LARGE:
        Message.error(i18n.get('error.file-size-over', {
          size: RichTextEditor.MAX_FILE_SIZE,
        }));
        break;
      default:
        break;
    }
  }
  // ctr+s save
  onKeyDown = (e) => {
    const result = keyDownResult(e);
    if (result.save) {
      this.saveNote(undefined, undefined, true);
      e.preventDefault();
    }
  };

  // 从redux 中获取note信息
  getNote = (props = this.props) => {
    const { notes, match } = props;
    const { params: { noteId } } = match;
    return notes.getIn(['entities', noteId]);
  }
  // 从redux 中获取content，非从editor中
  getContent = (noteId) => {
    const { content } = this.props;
    if (noteId) {
      return cleanTheNoteHtml(content.getIn(['entities', noteId.toString(), 'content']));
    }
    return '';
  }
  // 获取编辑器当前选中的linkInfo
  getCurrentLinkInfo = () => {
    return this.editor.getCurrentLinkInfo();
  }
  // 当前光标是否选中了link元素
  hasSelectLink = () => {
    return this.editor.hasSelectLink();
  }
  onModalCancelClick = () => {
    this.setState({
      linkModalVisible: false,
      videoModalVisible: false,
      audioModalVisible: false,
      imgModalVisible: false,
      formulaModalVisible: false,
      filmModalVisible: false,
    });
    this.editor.focus();
  }
  onModalRemoveLinkBtnClick = () => {
    this.editor.exec('removeLink');
    this.onModalCancelClick();
  }
  onModalConfirmBtnClick = (modalName, data = {}) => {
    const { url, text, files, videoInfo, title, dubber, noteId, formula, film, score } = data;

    switch (modalName) {
      case 'linkModal':
        this.editor.exec('insertLink', { url, text });
        break;
      case 'imgModal':
        this.editor.exec('insertImgs', { images: files });
        break;
      case 'videoModal':
        this.editor.exec('insertVideo', videoInfo);
        break;
      case 'audioModal':
        this.props.putAudioMessage(noteId, title, dubber);
        break;
      case 'formulaModal':
        this.editor.exec('insertFormula', { formula });
        break;
      case 'filmModal':
        this.insertFilm(film, score);
        break;
      default:
        break;
    }
    // 关闭弹窗
    this.onModalCancelClick();
  }
  // 插入电影海报图片，并在图片后面跟上评分文字
  insertFilm = (film, score) => {
    this.editor.exec('insertImgs', { images: [{ url: film.poster }] });
    const editElem = this.editor.$editElem[0];
    const posterImgs = editElem.querySelectorAll(`.image-package img[src="${film.poster}"]`);
    const posterImg = posterImgs.length > 0 ? posterImgs[posterImgs.length - 1] : null;
    const imgContainer = posterImg ? posterImg.closest('.image-package') : null;
    if (!imgContainer) {
      return;
    }
    // 给海报图外面包一层电影详情链接，点击海报可跳转到电影详情页；
    // 依赖 dante.no.css.js 的两处 vendor patch：
    //   1. clearMediaElement 白名单放行 image-package 直接子节点 <a>
    //   2. getContent 空链接移除时跳过含 <img> 的 <a>
    // 只在尚未包裹过时执行，避免重复插入时嵌套多层 <a>
    if (film.id && posterImg.parentNode.tagName !== 'A') {
      const filmLink = document.createElement('a');
      filmLink.href = `https://www.jianshu.com/film/${film.id}`;
      filmLink.target = '_blank';
      filmLink.rel = 'noopener noreferrer';
      posterImg.parentNode.insertBefore(filmLink, posterImg);
      filmLink.appendChild(posterImg);
    }
    // 评分作为 image-package 的同级段落并带 film-score 类：class 能穿过序列化，
    // 编辑/预览态居中由应用样式负责；发布态的居中内联样式在保存字符串时注入
    // （Dante 会剥掉顶层节点的内联 style，也会删除 data: 占位 img 及其容器）
    const scoreElem = document.createElement('p');
    scoreElem.className = 'film-score';
    scoreElem.textContent = getFilmScoreText(score);
    imgContainer.parentNode.insertBefore(scoreElem, imgContainer.nextSibling);
    // 光标落到评分段之后的普通段落：避免后续输入接进评分文字，
    // 也避免在评分段内回车拆分段落时新行继承 film-score 类变成居中
    let nextElem = scoreElem.nextElementSibling;
    if (!nextElem || nextElem.tagName !== 'P') {
      nextElem = document.createElement('p');
      nextElem.innerHTML = '<br>';
      scoreElem.parentNode.insertBefore(nextElem, scoreElem.nextSibling);
    }
    this.editor.selection.setCursorBeforeElem({ 0: nextElem, length: 1 });
    this.editor.handleHtmlChange(false);
  }
  openLinkModal = () => {
    const curLinkInfo = this.getCurrentLinkInfo();
    this.setState({
      hasSelectLink: this.hasSelectLink(),
      linkModalVisible: true,
      // 这样写清楚点，简写容易造成看不清依赖
      curLinkInfo: {
        url: curLinkInfo.url,
        text: curLinkInfo.text,
      },
    });
  }
  openImageModal = () => {
    this.setState({
      imgModalVisible: true,
    });
  }
  openVideoModal = () => {
    if (this.editor.getVideoCount() > MAX_VIDEO_COUNT) {
      Message.error('error.video-max-over');
      return;
    }
    this.setState({
      videoModalVisible: true,
    });
  }
  openAudioModal = () => {
    const { isAudioExist } = this.state;
    const { userInfo } = this.props;
    const canUploadAudio = userInfo.getIn(['can_upload_audio']);
    const jsdLevel = userInfo.getIn(['jsd_level_for_upload_audio', 'min_score']);
    if (!canUploadAudio) {
      Modal.open(
        (
          <Fragment>
            <p className={styles['audio-explain-modal-content']}>
              {i18n.get('modal.insert-audio-explain', { minJSD: (jsdLevel / 1000).toFixed(0) })}
            </p>
            <div className={styles['audio-explain-modal-btn']}>
              <a className={styles['audio-explain-modal-btn-about']} href={'/mobile/fp/growth'} target='_blank'>{i18n.get('pay-note.about-jsd')}</a>
              <a className={styles['audio-explain-modal-btn-buy']} href={'/mobile/club'} target='_blank'>{i18n.get('pay-note.open-js-member')}</a>
            </div>
          </Fragment>
        ), {
          showFooter: false,
          title: i18n.get('modal.insert-audio-modal'),
          headerStyle: { textAlign: 'center' },
          tips: false,
          width: 360,
          top: '30%',
        },
      );
      return null;
    }
    this.setState({
      audioModalVisible: true,
      isAudioPlaying: false,
    }, () => {
      if (isAudioExist) {
        this.audio.pause();
      }
    });
    return null;
  }
  openFormulaModal = (formula) => {
    this.setState({
      formulaModalVisible: true,
      initFormula: formula,
    });
  }
  openFilmModal = () => {
    this.setState({
      filmModalVisible: true,
    });
  }
  onAudioPlay = (e) => {
    const { content } = this.props;
    const { match: { params: { noteId } } } = this.props;
    const playUrl = content.getIn(['entities', noteId.toString(), 'audio', 'play_url']);
    if (playUrl === '') {
      Modal.Confirm(`${i18n.get('modal.audio-transcoding')}`, {
        onOk: () => {},
        showCancelBtn: false,
      });
      return;
    }
    const changePlayType = () => {
      this.setState({
        isAudioPlaying: false,
      });
      this.audio.removeEventListener('ended', changePlayType);
    };
    this.setState({
      isAudioPlaying: !this.state.isAudioPlaying,
    }, () => {
      if (this.state.isAudioPlaying) {
        this.audio.addEventListener('ended', changePlayType);
        this.audio.play();
      } else {
        this.audio.removeEventListener('ended', changePlayType);
        this.audio.pause();
      }
    });
  }
  publicizePaidNote = (nbId, noteId, isCompile) => {
    const { history, userInfo } = this.props;
    const content = this.getContent(noteId);
    if (getChildrenNum(content) < 2) {
      Message.error(i18n.get('error.content-not-enough', { num: 2 }));
      return;
    }
    if (isCompile) {
      JsSensor.trackEvent(SAEVENTS.COMPILE_PAID_NOTE_BTN_CLICK);
    } else {
      JsSensor.trackEvent(SAEVENTS.PUBLISH_PAID_NOTE_BTN_CLICK);
    }
    history.push(getPNSettingPageUrl(nbId, noteId));
  }

  publicNote = (noteId, { isCompile, forcedPublish }) => {
    const { content } = this.props;
    const playUrl = content.getIn(['entities', noteId.toString(), 'audio', 'play_url']);
    if (playUrl === '') {
      Modal.Alert(i18n.get('modal.audio-transcoding'));
      return;
    }
    if (this.editor.isUploadingImage()) {
      Message.error(i18n.get('error.uploading-img'));
      return;
    }
    this.props.publishNote(noteId, forcedPublish);
    // 更新文章
    if (isCompile) {
      JsSensor.trackEvent(SAEVENTS.COMPILE_NOTE_BTN_CLICK);
    } else {
      JsSensor.trackEvent(SAEVENTS.PUBLISH_NOTE_BTN_CLICK);
    }
  };

  publicizeNote = (noteId, isCompile) => {
    const note = this.getNote();
    const isSetTimePublish = !!note.get('schedule_publish_at');
    if (isSetTimePublish) {
      Modal.Confirm('该文章已设置成定时发布，确定现在就要发布么？', {
        onOk: () => {
          this.publicNote(noteId, { isCompile, forcedPublish: true });
        },
      });
    } else {
      this.publicNote(noteId, { isCompile });
    }
  };

  onToolItemClick = (action, data = {}) => {
    const { history, match, content } = this.props;
    const { params: { nbId, noteId } } = match;
    const { level } = data;
    const audioUrl = content.getIn(['entities', noteId, 'audio', 'play_url']);
    switch (action) {
      case 'publicize-paid-note':
        this.publicizePaidNote(nbId, noteId);
        break;
      case 'compile-paid-note':
        this.publicizePaidNote(nbId, noteId, true);
        break;
      case 'publicize':
          this.publicizeNote(noteId);
        return;
      case 'compile':
        this.publicizeNote(noteId, true);
        return;
      case 'privatize':
        this.props.privateNote(noteId);
        return;
      case 'auto-save':
        this.saveNote(undefined, undefined, true);
        return;
      case 'openLinkModal':
        this.openLinkModal();
        return;
      case 'openImageModal':
        this.openImageModal();
        return;
      case 'openVideoModal':
        this.openVideoModal();
        return;
      case 'openAudioModal':
        this.openAudioModal();
        return;
      case 'openFormulaModal':
        this.openFormulaModal('');
        return;
      case 'openFilmModal':
        this.openFilmModal();
        return;
      case 'to-fullscreen':
        history.push(`/notebooks/${nbId}/notes/${noteId}/writing`);
        return;
      case 'exit-fullscreen':
        history.push(`/notebooks/${nbId}/notes/${noteId}`);
        return;
      case 'to-history':
        history.push(`/notebooks/${nbId}/notes/${noteId}/history`);
        return;
      default:
        break;
    }
    if (action) {
      this.editor.exec(action, level);
    }
  }
  getNoteAttr(attrName) {
    const note = this.getNote();
    return (note && note.get(attrName)) || false;
  }
  render() {
    const {
      isContentChanged,
      isPublishing,
      isAutoSaving,
      linkModalVisible,
      imgModalVisible,
      videoModalVisible,
      audioModalVisible,
      formulaModalVisible,
      filmModalVisible,
      curLinkInfo,
      wordNum,
      isDraggingOver,
      hasSelectLink,
      isAudioExist,
      isAudioPlaying,
      initFormula,
    } = this.state;
    const { className, match, isWritingMode, notes, content, userInfo } = this.props;
    const { params: { noteId }, path } = match;
    const note = this.getNote();
    const wrapperClass = classNames(className, {
      [styles['editor-normal-mode']]: !isWritingMode,
      [styles['editor-writing-mode']]: isWritingMode,
    });
    const toolCustomerClass = classNames({
      [styles['tool-bar__writingmode']]: isWritingMode,
    });
    const containerClass = classNames(styles['editor-container'], {
      [styles['dragging-over']]: isDraggingOver,
    });
    const audioPlay = classNames('fa fa-play-circle-o fa-2x', {
      [styles['hidden']]: isAudioPlaying,
    });
    const audioPause = classNames('fa fa-pause-circle-o fa-2x', {
      [styles['hidden']]: !isAudioPlaying,
    });
    const isNoteShared = this.getNoteAttr('shared');
    const isPaidNote = this.getNoteAttr('paid');
    return (
      <div className={wrapperClass} tabIndex={-1} onKeyDown={this.onKeyDown}>
        <EditorTitle isWritingMode={isWritingMode} onTitleChanged={this.onTitleChanged}/>
        <EditorToolBar
          isPaidNote={isPaidNote}
          isNoteShared={isNoteShared}
          isWritingMode={isWritingMode}
          onToolItemClick={this.onToolItemClick}
          contentChanged={isContentChanged}
          isPublishing={isPublishing}
          isAutoSaving={isAutoSaving}
          customerClass={toolCustomerClass}
          isMarkDown={false}
          wordNum={wordNum}
          isAudioExist={isAudioExist}
        />
        <TimePublishToolbar note={note}/>
        <div className={styles['scroll-container']}>
          {
            isAudioExist &&
            <div className={styles['audio-container']}>
              <div className={styles['audio-player-content']}>
                <label className={styles['audio-player']} htmlFor="audio-player" onClick={this.onAudioPlay}>
                  <i className={audioPlay}/>
                  <i className={audioPause}/>
                </label>
                <audio id="audio-player" src={content.getIn(['entities', noteId.toString(), 'audio', 'play_url'])} className={styles['audio']} ref={(audio) => { this.audio = audio; }}/>
                <span className={styles['audio-message']}>
                  <span className={styles['audio-title']}>{content.getIn(['entities', noteId, 'audio', 'title'])}</span>
                  <span className={styles['audio-dubber']}>{content.getIn(['entities', noteId, 'audio', 'dubber'])}</span>
                </span>
                <span className={styles['audio-editor']} onClick={this.openAudioModal}>
                  {i18n.get('modal.audio-edit')}
                </span>
              </div>
            </div>
          }
          <div className={containerClass} id='editor'/>
        </div>
        {
          // modal虽然放在这里，但是却是挂在body下,
          // 本来打算放在toolbar组件里, 但是toolbar知道关于editor的信息实在是太少了
          // 还是放在这里好些
          // 让modal每次都是重新挂在，方便设置input的初始值
        }
        {
          linkModalVisible &&
          <EditorLinkModal
            onModalCancelClick={this.onModalCancelClick}
            onModalConfirmBtnClick={this.onModalConfirmBtnClick}
            onModalRemoveLinkBtnClick={this.onModalRemoveLinkBtnClick}
            hasSelectLink={hasSelectLink}
            urlValue={curLinkInfo.url}
            textValue={curLinkInfo.text}
          />
        }
        {
          imgModalVisible &&
          <EditorImgModal
            visible={imgModalVisible}
            onModalCancelClick={this.onModalCancelClick}
            onModalConfirmBtnClick={this.onModalConfirmBtnClick}
          />
        }
        {
          videoModalVisible &&
          <EditorVideoModal
            visible={videoModalVisible}
            onModalCancelClick={this.onModalCancelClick}
            onModalConfirmBtnClick={this.onModalConfirmBtnClick}
          />
        }
        {
          audioModalVisible &&
          <EditorAudioModal
            audioMessage={content.getIn(['entities', noteId, 'audio'])}
            visible={audioModalVisible}
            changeAudio={this.changeAudio}
            onModalCancelClick={this.onModalCancelClick}
            onModalConfirmBtnClick={this.onModalConfirmBtnClick}
          />
        }
        {
          formulaModalVisible &&
          <EditorFormulaModal
            visible={formulaModalVisible}
            initFormula={initFormula}
            onModalCancelClick={this.onModalCancelClick}
            onModalConfirmBtnClick={this.onModalConfirmBtnClick}
          />
        }
        {
          filmModalVisible &&
          <EditorFilmModal
            visible={filmModalVisible}
            onModalCancelClick={this.onModalCancelClick}
            onModalConfirmBtnClick={this.onModalConfirmBtnClick}
          />
        }
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/Editor/RichTextEditor.js